import React, { useMemo } from 'react';
import { Question } from '../../../types';
import { generateSupportCoverageReport, FamilyCoverageStatus } from '../../../support/supportCoverageReport';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { CheckCircle2, AlertTriangle, AlertOctagon, PieChart, HelpCircle, Eye, FileQuestion, LayoutList, XCircle } from 'lucide-react';
interface TaxonomyCoverageProps {
  questions: Question[];
}
export function TaxonomyCoverage({
  questions
}: TaxonomyCoverageProps) {
  const report = useMemo(() => generateSupportCoverageReport(questions), [questions]);
  const {
    summary,
    byModule,
    familyStatuses
  } = report;
  const getStatusColor = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return 'text-emerald-500';
      case 'partial':
        return 'text-amber-500';
      case 'missing_support':
        return 'text-rose-500';
      case 'missing_questions':
        return 'text-blue-500';
      case 'empty':
        return 'text-slate-300';
    }
  };
  const getStatusIcon = (status: FamilyCoverageStatus['status']) => {
    switch (status) {
      case 'complete':
        return CheckCircle2;
      case 'partial':
        return AlertTriangle;
      case 'missing_support':
        return AlertOctagon;
      case 'missing_questions':
        return FileQuestion;
      case 'empty':
        return XCircle;
    }
  };
  return <div className="space-y-8" data-id="element-1256">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1257">
        <SummaryCard title="Complete" count={summary.complete} total={report.totalFamilies} icon={CheckCircle2} color="emerald" data-id="element-1258" />

        <SummaryCard title="Partial" count={summary.partial} total={report.totalFamilies} icon={AlertTriangle} color="amber" data-id="element-1259" />

        <SummaryCard title="No Support" count={summary.missingSupport} total={report.totalFamilies} icon={AlertOctagon} color="rose" data-id="element-1260" />

        <SummaryCard title="No Questions" count={summary.missingQuestions} total={report.totalFamilies} icon={FileQuestion} color="blue" data-id="element-1261" />

        <SummaryCard title="Empty" count={summary.empty} total={report.totalFamilies} icon={XCircle} color="slate" data-id="element-1262" />
      </div>

      {/* Module Breakdown */}
      <Card className="p-6 border-slate-200" data-id="element-1263">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1264">
          <LayoutList className="w-5 h-5 text-indigo-600" data-id="element-1265" />
          Module Breakdown
        </h3>
        <div className="overflow-x-auto" data-id="element-1266">
          <table className="w-full text-sm text-left" data-id="element-1267">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1268">
              <tr data-id="element-1269">
                <th className="px-4 py-3 font-bold" data-id="element-1270">Module</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1271">Total</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1272">Supported</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1273">Missing</th>
                <th className="px-4 py-3 font-bold" data-id="element-1274">Coverage</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1275">
              {Object.entries(byModule).map(([mod, stats]) => {
              const percentage = Math.round(stats.supported / stats.total * 100);
              return <tr key={mod} className="hover:bg-slate-50/50" data-id="element-1276">
                    <td className="px-4 py-3 font-medium text-slate-900" data-id="element-1277">
                      {mod}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600" data-id="element-1278">
                      {stats.total}
                    </td>
                    <td className="px-4 py-3 text-center text-emerald-600 font-bold" data-id="element-1279">
                      {stats.supported}
                    </td>
                    <td className="px-4 py-3 text-center text-rose-600 font-bold" data-id="element-1280">
                      {stats.missing.length}
                    </td>
                    <td className="px-4 py-3" data-id="element-1281">
                      <div className="flex items-center gap-3" data-id="element-1282">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1283">
                          <div className={`h-full rounded-full ${percentage === 100 ? 'bg-emerald-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{
                        width: `${percentage}%`
                      }} data-id="element-1284" />
                        </div>
                        <span className="text-xs font-bold text-slate-500 w-8 text-right" data-id="element-1285">
                          {percentage}%
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Critical Issues List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1286">
        {/* Missing Support */}
        <Card className="p-6 border-rose-200 bg-rose-50/10" data-id="element-1287">
          <h3 className="font-bold text-rose-900 mb-4 flex items-center gap-2" data-id="element-1288">
            <AlertOctagon className="w-5 h-5 text-rose-600" data-id="element-1289" />
            Missing Support ({report.questionsWithoutSupport.length})
          </h3>
          <p className="text-xs text-rose-700 mb-4" data-id="element-1290">
            These families have runnable questions but NO support content
            defined.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1291">
            {report.questionsWithoutSupport.length === 0 ? <div className="text-sm text-emerald-600 font-medium flex items-center gap-2" data-id="element-1292">
                <CheckCircle2 className="w-4 h-4" data-id="element-1293" /> All active families have
                support!
              </div> : report.questionsWithoutSupport.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-rose-100 text-sm" data-id="element-1294">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1295">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1296">
                    Needs Entry
                  </Badge>
                </div>)}
          </div>
        </Card>

        {/* Missing Questions */}
        <Card className="p-6 border-blue-200 bg-blue-50/10" data-id="element-1297">
          <h3 className="font-bold text-blue-900 mb-4 flex items-center gap-2" data-id="element-1298">
            <FileQuestion className="w-5 h-5 text-blue-600" data-id="element-1299" />
            Unused Support ({report.supportWithoutQuestions.length})
          </h3>
          <p className="text-xs text-blue-700 mb-4" data-id="element-1300">
            Support content exists, but no questions use this family ID yet.
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1301">
            {report.supportWithoutQuestions.length === 0 ? <div className="text-sm text-slate-500 italic" data-id="element-1302">
                No orphaned support entries.
              </div> : report.supportWithoutQuestions.map(id => <div key={id} className="flex items-center justify-between p-2 bg-white rounded border border-blue-100 text-sm" data-id="element-1303">
                  <span className="font-mono font-bold text-slate-700" data-id="element-1304">
                    {id}
                  </span>
                  <Badge variant="neutral" size="sm" data-id="element-1305">
                    Needs Questions
                  </Badge>
                </div>)}
          </div>
        </Card>
      </div>

      {/* Detailed Family Status Table */}
      <Card className="p-6 border-slate-200" data-id="element-1306">
        <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1307">
          <PieChart className="w-5 h-5 text-indigo-600" data-id="element-1308" />
          Detailed Family Status
        </h3>
        <div className="overflow-x-auto" data-id="element-1309">
          <table className="w-full text-sm text-left" data-id="element-1310">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-1311">
              <tr data-id="element-1312">
                <th className="px-4 py-3 font-bold" data-id="element-1313">Family ID</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1314">Status</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1315">Questions</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1316">Hints</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1317">Visual</th>
                <th className="px-4 py-3 font-bold text-center" data-id="element-1318">Explain</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1319">
              {familyStatuses.map(status => {
              const StatusIcon = getStatusIcon(status.status);
              const statusClass = getStatusColor(status.status);
              return <tr key={status.familyId} className="hover:bg-slate-50/50" data-id="element-1320">
                    <td className="px-4 py-3" data-id="element-1321">
                      <div className="font-mono font-bold text-slate-700 text-xs" data-id="element-1322">
                        {status.familyId}
                      </div>
                      <div className="text-xs text-slate-500 truncate max-w-[200px]" data-id="element-1323">
                        {status.description}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1324">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${statusClass.replace('text-', 'bg-').replace('500', '50')} ${statusClass} border-${statusClass.split('-')[1]}-200`} data-id="element-1325">
                        <StatusIcon className="w-3.5 h-3.5" data-id="element-1326" />
                        {status.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1327">
                      {status.questionCount > 0 ? <span className="font-bold text-slate-700" data-id="element-1328">
                          {status.questionCount}
                        </span> : <span className="text-slate-300" data-id="element-1329">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1330">
                      {status.hasHints ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1331" /> : status.requiresHints ? <span className="text-rose-500 font-bold text-xs" data-id="element-1332">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1333">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1334">
                      {status.hasVisual ? <Eye className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1335" /> : status.requiresVisual ? <span className="text-rose-500 font-bold text-xs" data-id="element-1336">
                          REQ
                        </span> : <span className="text-slate-300" data-id="element-1337">-</span>}
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1338">
                      {status.hasExplanation ? <HelpCircle className="w-4 h-4 text-emerald-500 mx-auto" data-id="element-1339" /> : <span className="text-slate-300" data-id="element-1340">-</span>}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}
function SummaryCard({
  title,
  count,
  total,
  icon: Icon,
  color
}: {
  title: string;
  count: number;
  total: number;
  icon: React.ElementType;
  color: 'emerald' | 'amber' | 'rose' | 'blue' | 'slate';
}) {
  const colorClasses: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
    rose: 'text-rose-600 bg-rose-50',
    blue: 'text-blue-600 bg-blue-50',
    slate: 'text-slate-600 bg-slate-50'
  };
  return <Card className="p-4 flex items-center justify-between border-slate-200" data-id="element-1341">
      <div data-id="element-1342">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1343">
          {title}
        </div>
        <div className="text-2xl font-bold text-slate-900" data-id="element-1344">{count}</div>
        <div className="text-xs text-slate-500 mt-1" data-id="element-1345">
          {Math.round(count / total * 100)}% of total
        </div>
      </div>
      <div className={`p-3 rounded-full ${colorClasses[color]}`} data-id="element-1346">
        <Icon className="w-6 h-6" data-id="element-1347" />
      </div>
    </Card>;
}